import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Optional,
  Output,
  forwardRef
} from '@angular/core';
import { AbstractControl, ControlContainer, ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';

export interface VivaToggleOption {
  display?: string;
  value: any;
  src?: string;
  imagePosition?: 'start' | 'end';
  width?: string;
}

@Component({
  selector: 'viva-toggle',
  templateUrl: './viva-toggle.component.html',
  styleUrls: ['./viva-toggle.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VivaToggleComponent),
      multi: true
    }
  ],
  standalone: false
})
export class VivaToggleComponent implements OnInit, AfterViewInit, ControlValueAccessor {
  @Input() control: AbstractControl | null = null;
  @Input() formControlName: string | number | null = null;
  @Input() options?: VivaToggleOption[];
  @Input() sameSize: string = "true";
  @Input() required?: string; // Required property as string
  @Input() disabled: boolean = false; // Disabled property

  @Output() valueChange = new EventEmitter<any>();
  @Input() onkeydown?: (event: KeyboardEvent) => boolean;

  private _value: any = '';
  @Input()
  get value(): any {
    return this._value;
  }
  set value(val: any) {
    this._value = val;
    this.onChange(val);
    this.onTouched();
    this.valueChange.emit(val);
    if (this.control) {
      this.control.setValue(val);
      this.control.markAsDirty();
      this.control.updateValueAndValidity();
    }
  }

  private onChange: (value: any) => void = () => { };
  private onTouched: () => void = () => { };

  constructor(
    @Optional() private controlContainer: ControlContainer,
    private cdr: ChangeDetectorRef,
    private elRef: ElementRef,
    private zone: NgZone
  ) { }

  ngOnInit() {
    if (!this.control && this.controlContainer) {
      const formGroup = this.controlContainer.control as FormGroup;
      if (this.formControlName && formGroup) {
        const controlName = this.formControlName;
        this.control = formGroup.get(controlName as any) as FormControl;
        if (this.isRequired()) {
          this.control.setValidators(Validators.required);
        }
      }
    }
  }

  async ngAfterViewInit() {
    this.writeValue(this._value);
  }

  writeValue(value: any): void {
    if (value !== undefined) {
      this._value = value;
      this.cdr.detectChanges();
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.detectChanges();
  }

  selectOption(option: VivaToggleOption): void {
    if (!this.disabled) {
      this.value = option.value;
    }
  }

  get isInvalid(): boolean {
    return this.isRequired() && !this.value;
  }

  get sameSizeClass(): boolean {
    return this.sameSize !== 'false';
  }

  getOptionClasses(option: VivaToggleOption): object {
    return {
      selected: option.value === this.value,
    };
  }

getOptionStyles(option: VivaToggleOption): { [klass: string]: any } {
  return option.width
    ? { width: option.width }
    : { flex: '1 1 0' }; // Only fallback if no width
}

  private isRequired(): boolean {
    return this.required !== undefined && this.required !== null;
  }
}
